import AppBar from '@mui/material/AppBar';
import { Container, Grid, useMediaQuery, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import FooterLogo from 'app/layouts/whitelabel/components/footer/FooterLogo';
import { footerConfig, getFooterMenuWidths, SECONDARY_LOGO_MD, TERTIARY_LOGO_MD } from 'app/configs/footerConfig';
import { getLogo } from '../helpers/Generichelper';
import SocialMediaIcons from '../../../shared-components/SocialMediaIcons/SocialMediaIcons';
import { getChannelSettingByKey } from '../../routes/generic/Helpers/GenericIndexHelper';
import FooterLayoutAdvanced, { getVisibleAdvancedFooters } from './FooterLayoutAdvanced';
import { LOGO_CAPTION_MAX_WIDTH, useStylesFooterLayout } from '../styles/FooterStyles';
import { getNewBuyInsurancePathGeneric } from '../../routes/generic/Helpers/HomePageHelper';
import { getGALabel } from '../../routes/generic/Helpers/GenericHelper';

const MenuRow = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-start;
`;

const MenuHeader = styled.div`
	font-size: 1.6rem;
	font-weight: 700;
	line-height: 1.4;
	margin-bottom: 2rem;
	color: ${({ theme }) => theme.palette.footer.headerFontColor};
`;

// `anywhere`, not `break-word`: MenuRow is align-items:flex-start so each item is shrink-to-fit, and
// break-word does not reduce min-content width - the box would still size to the full unbroken word
// and spill into the next column. The contact column is the tight one, at roughly 167px against a
// 162px email address.
const MenuItem = styled.div`
	margin: 0 0 1.2rem;
	font-size: 1.4rem;
	line-height: 1.6;
	overflow-wrap: anywhere;
	color: ${({ theme }) => theme.palette.footer.fontColor};
`;

const LinkText = styled.span`
	color: ${({ theme }) => theme.palette.footer.fontColor} !important;
	transition: opacity 150ms ease;

	&:hover {
		opacity: 0.75;
	}
`;

// Mirrors the navbar's Container + Toolbar gutters (16+16 at xs, 24+24 above it) so the header and
// footer share a left and right edge.
const CONTAINER_PX = { xs: 4, sm: 6 };

// The gap between columns, in MUI spacing units. MUI applies it as a negative margin on the Grid
// plus padding on each item, so it has to stay at or below CONTAINER_PX above: a larger value bleeds
// past the viewport, and body{overflow-x:hidden} clips that silently rather than showing a
// scrollbar. 20px also leaves each menu column ~167px of content - the row is (2/12)(content + gap)
// minus the gap - which is what keeps a full-length email address on one line now that the contact
// column is 2 units rather than 3.
const COLUMN_GUTTER = 2.5;

// Depends on nothing in the component, so it lives beside the other style declarations rather than
// being rebuilt on every render.
const linkStyle = {
	background: 'none',
	textDecoration: 'none',
	borderBottom: 'none'
};

function FooterSecondLogo({ tertiaryLogo, tertiaryLogoText, tertiaryLogoTextColor }) {
	const logo = {
		marginTop: '10px',
		height: 'auto',
		width: 'auto',
		maxHeight: '100px',
		maxWidth: '120px'
	};
	const figcaption = {
		marginTop: '5px',
		fontSize: '10px',
		textAlign: 'center',
		color: tertiaryLogoTextColor,
		maxWidth: LOGO_CAPTION_MAX_WIDTH
	};
	const figContainer = {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		flexDirection: 'column'
	};

	return (
		<figure style={figContainer}>
			{tertiaryLogo && <img style={logo} src={tertiaryLogo} alt="" />}
			<figcaption style={figcaption}>{tertiaryLogoText}</figcaption>
		</figure>
	);
}

function FooterLayout(props) {
	const theme = useTheme();
	// A media query rather than useWindowDimensions: the latter re-renders the whole footer on every
	// resize pixel, which defeats the memo() below and re-runs the useMemo dependency check each
	// pixel. The only thing that needs the viewport is the phone link, which changes once at 600.
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
	const classes = useStylesFooterLayout(isMobile, theme);
	const channelSettings = useSelector(({ fuse }) => fuse.form.channelSettings);
	const companyDetailsResponse = useSelector(({ fuse }) => fuse.form.companyDetailsResponse);
	const agentDetails = useSelector(({ fuse }) => fuse?.form?.referenceTokenDetails?.agentDetails);
	const channelProducts = useSelector(({ fuse }) => fuse.form.channelProducts);
	const referenceTokenDetails = useSelector(({ fuse }) => fuse.form.referenceTokenDetails);
	const myHostPrefix = useSelector(({ fuse }) => fuse.form.mySite.hostPrefix);
	const retryPaymentResponse = useSelector(({ fuse }) => fuse.form.retryPaymentResponse);

	// footerConfig scans the whole channelSettings array through getSettings()'s 62-case switch and
	// then calls getChannelSettingByKey nine more times. useWindowDimensions is unthrottled, so
	// without this memo that entire derivation re-runs on every pixel of a window resize. The deps
	// are the store slices footerConfig reads internally via store.getState(), which is why they do
	// not appear in the callback body.
	const footerItems = useMemo(
		() => footerConfig({ companyDetailsResponse }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[companyDetailsResponse, channelSettings, channelProducts, referenceTokenDetails, myHostPrefix]
	);
	const hideFooterMenuItems =
		retryPaymentResponse?.value || getChannelSettingByKey('HIDE-FOOTER-MENU-ITEMS') || 'false';
	const hideFirstFooter = getChannelSettingByKey('Footer-First-Hide') === 'true';
	// Asks the advanced footer itself, rather than re-reading the two hide flags, so the wrapper below
	// cannot render its 10px margin over a section that turns out to have nothing in it.
	const visibleAdvancedFooters = getVisibleAdvancedFooters();
	const showAdvancedFooter = visibleAdvancedFooters.second || visibleAdvancedFooters.third;
	const secondaryLogoSetting = getChannelSettingByKey('Secondary-Logo');
	const tertiaryLogoSetting = getChannelSettingByKey('Tertiary-Logo');
	// Both logo captions read this one setting, so they resolve the fallback the same way here
	// instead of each picking its own. `||` rather than `??`: getChannelSettingByKey returns the raw
	// stored value, so a channel holding the setting but leaving it blank yields '', which `??`
	// passes through. The old literal 'black' fallback also rendered invisibly on a dark footer.
	const tertiaryLogoTextColor = getChannelSettingByKey('Footer-Logo-Text-Color') || theme.palette.footer.fontColor;

	// getLogo rebuilds its result by scanning the entire channelSettings array, so it is called once
	// and destructured instead of once per field. agentLogoType 3 means the agent supplies its own
	// tertiary branding, which overrides both the channel's logo and its caption.
	const { tertiaryLogo: channelTertiaryLogo, tertiaryLogoText: channelTertiaryLogoText } = getLogo() ?? {};
	const hasAgentTertiaryLogo = agentDetails?.agentLogoType === 3;
	const tertiaryLogo =
		hasAgentTertiaryLogo && agentDetails?.agentLogoBlobUrl ? agentDetails.agentLogoBlobUrl : channelTertiaryLogo;
	const tertiaryLogoText =
		hasAgentTertiaryLogo && agentDetails?.agentLogoText ? agentDetails.agentLogoText : channelTertiaryLogoText;
	const buyInsurancePath = getNewBuyInsurancePathGeneric(channelProducts, referenceTokenDetails?.token, myHostPrefix);

	// Read through the local channelSettings subscription rather than getChannelSettingByKey, which
	// hits store.getState() directly and would not re-render the footer once settings arrive.
	const checkIsVisible = key => {
		return channelSettings?.value?.channelSettings.find(_key => _key.key === key)?.value;
	};

	// Every footer link is gated by its own channel setting. Holding the url -> setting map in one
	// place lets a column's emptiness be decided before render, so a channel that has hidden all of
	// a group's links no longer gets a heading floating over a blank column.
	const menuVisibilitySettingKeys = {
		'/faq': 'FooterMenu-FAQ-Visible',
		'/terms-and-conditions': 'FooterMenu-Terms-And-Conditions-Visible',
		'/privacy-policy': 'FooterMenu-PrivacyPolicy-Visible',
		'/disclosure': 'FooterMenu-Disclosure-Visible',
		'/details': 'FooterMenu-Details-Visible',
		'/contact': 'FooterMenu-Contact-Visible',
		'/about': 'FooterMenu-About-Visible',
		'/submit-claim': 'FooterMenu-Claim-Visible',
		[buyInsurancePath]: 'FooterMenu-Insurance-Visible'
	};

	// mailTo and phone entries carry no gate, matching the previous switch's default branch. Blank
	// titles are dropped because the address column emits empty rows for a channel that has no
	// phone number or email on file, and those become visible gaps at the new item spacing.
	const isMenuItemVisible = c => {
		if (!c?.title?.trim()) return false;
		if (c?.url?.slice(0, 6) === 'mailTo' || c?.url?.slice(0, 1) === '+') return true;

		const settingKey = menuVisibilitySettingKeys[c?.url];
		return settingKey ? checkIsVisible(settingKey) === 'true' : true;
	};

	// Drop whole columns, not just their links: an empty Grid item still reserves its md width and
	// still counts as a flex line, so it would leave a phantom gap in the row.
	const visibleMenus = (footerItems?.menus ?? [])
		.map(item => ({ ...item, menuItems: (item?.menuItems ?? []).filter(isMenuItemVisible) }))
		.filter(item => item.menuItems.length > 0);

	// Widths come from what will actually render, so hidden columns hand their space back to the
	// visible ones instead of leaving the row short of 12 and centred.
	const showMenus = hideFooterMenuItems === 'false';
	const menuWidths = getFooterMenuWidths({
		hasSecondaryLogo: Boolean(secondaryLogoSetting),
		hasTertiaryLogo: Boolean(tertiaryLogoSetting),
		menuCount: showMenus ? visibleMenus.length : 0
	});

	// A row that still falls short of 12 (a single menu, no logos) is centred rather than left
	// hanging against the container edge.
	const columnTotal =
		menuWidths.reduce((sum, menuWidth) => sum + menuWidth, 0) +
		(secondaryLogoSetting ? SECONDARY_LOGO_MD : 0) +
		(tertiaryLogoSetting ? TERTIARY_LOGO_MD : 0);

	// mailTo and tel targets leave the SPA, so they need a real anchor; every other url is an in-app
	// route and has to go through <Link> or it forces a full page reload. Only the routed branch
	// carries a GA label, which is what the previous per-branch markup did. The shared MenuItem
	// wrapper and the key stay with the caller so this returns the anchor alone.
	const renderFooterLink = c => {
		if (c?.url?.slice(0, 6) === 'mailTo') {
			return (
				<a href={c.url} target="_blank" rel="noopener noreferrer" style={linkStyle}>
					<LinkText theme={theme}>{c.title}</LinkText>
				</a>
			);
		}

		if (c?.url?.slice(0, 1) === '+') {
			return (
				<a href={c.url} style={classes.Phone}>
					<LinkText theme={theme}>{c.title}</LinkText>
				</a>
			);
		}

		return (
			<Link to={c.url || '/'}>
				<LinkText theme={theme} data-ga-label={getGALabel({ name: 'footer-menu', label: c?.title })}>
					{c.title}
				</LinkText>
			</Link>
		);
	};

	return (
		<>
			{!hideFirstFooter && (
				<AppBar
					id="fuse-footer"
					className={clsx('z-20 shadow-md', props.className)}
					position="relative"
					color="default"
					sx={{ backgroundColor: theme.palette.footer.background }}
				>
					<Container sx={{ px: CONTAINER_PX, py: { xs: 5, md: 7 } }}>
						<Grid
							container
							columnSpacing={COLUMN_GUTTER}
							justifyContent={columnTotal >= 12 ? 'flex-start' : 'center'}
							alignItems="flex-start"
							sx={{ rowGap: 5 }}
						>
							{secondaryLogoSetting && (
								<Grid item xs={12} md={SECONDARY_LOGO_MD}>
									<FooterLogo />
								</Grid>
							)}
							{showMenus &&
								visibleMenus.map((item, itemIndex) => (
									<Grid item xs={item.xs} sm={item.sm} md={menuWidths[itemIndex]} key={item.id}>
										<MenuRow>
											<MenuHeader theme={theme}>{item.title}</MenuHeader>
											{item.menuItems.map(c => (
												<MenuItem theme={theme} key={c.url || c.title}>
													{renderFooterLink(c)}
												</MenuItem>
											))}
										</MenuRow>
									</Grid>
								))}
							{tertiaryLogoSetting && (
								<Grid className="flex flex-col items-center" item xs={12} md={TERTIARY_LOGO_MD}>
									<SocialMediaIcons />
									<FooterSecondLogo
										tertiaryLogo={tertiaryLogo}
										tertiaryLogoText={tertiaryLogoText}
										tertiaryLogoTextColor={tertiaryLogoTextColor}
									/>
								</Grid>
							)}
						</Grid>

						<div style={classes.divider} />

						<div style={classes.recaptchaText}>
							This site is protected by reCAPTCHA and the Google
							<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
								{' '}
								Privacy Policy
							</a>{' '}
							and
							<a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
								{' '}
								Terms of Service
							</a>{' '}
							apply.
						</div>
					</Container>
				</AppBar>
			)}

			{/* Advanced Footer Layout */}
			{showAdvancedFooter && (
				<div className="mt-10">
					<FooterLayoutAdvanced />
				</div>
			)}
		</>
	);
}

FooterSecondLogo.propTypes = {
	tertiaryLogo: PropTypes.string,
	tertiaryLogoText: PropTypes.string,
	tertiaryLogoTextColor: PropTypes.string
};

export default memo(FooterLayout);
